import { parseOrNull } from '../util/general';
import { redactUrl } from '../format/redactUrl';
import type { BasicFetch, RequestInit, Response } from '../types/basicFetch';
import createHttpError from 'http-errors';
import type { Logger } from '../types/logger';

interface FetchError extends Error {
  statusCode: number;
  status: number;
  info?: Record<string, unknown>;
}

export function isFetchError(err: unknown): err is FetchError {
  return err instanceof Error
    && 'statusCode' in err
    && typeof (err as Record<string, unknown>).statusCode === 'number';
}

export async function fetchOrThrow(logger: Logger, fetch: BasicFetch, url: string, init?: RequestInit): Promise<Response> {
  init = init ?? {};
  const response = await fetch(url, init);
  await throwIfHttpError(logger, url, init, response);
  return response;
}

async function throwIfHttpError(logger: Logger, url: string, init: RequestInit, response: Response) {
  if (response.ok) {
    return;
  }

  const info: Record<string, unknown> = {
    url: redactUrl(url),
    method: init.method,
  };

  if (response.status === 429) {
    /*
      Request Header Fields Too Large

      Need to include headers in the error so that we can debug this.
    */
    info.headers = response.headers;
  }

  let errorMessage = '';
  let error: Error;
  try {
    const errorBody = await response.text();
    const errorObj = parseOrNull<any>(logger, errorBody);
    if (errorObj) {
      Object.assign(info, errorObj);
      if (errorObj.error && typeof errorObj.error.message === 'string') {
        errorMessage = errorObj.error.message;
      }
    } else if (errorBody) {
      info.body = errorBody;
    }
  } catch (e) {
    // Pass.
  }
  try {
    error = createHttpError(response.status, errorMessage || response.statusText, { info });
  } catch {
    // In case createHttpError fails due to unimplemented captureStackTrace, see #10635
    error = new Error(errorMessage || response.statusText);
  }
  throw error;
}
